import { computed, ref, watch, onBeforeUnmount } from 'vue';
import { useAuth } from '@/auth/useAuth';
import { useCurrentTenant } from '@/composables/schoolAdmin/useCurrentTenant';
import { useWhoAmI } from '@/composables/useWhoAmI';
import { useSchoolClasses } from '@/composables/useSchoolClasses';

/**
 * Unified readiness gate for growth popups.
 *
 * Flips `isGrowthReady` to true once both:
 *   1. Core session data has loaded (login + tenant + whoAmI + schoolClasses), and
 *   2. No chart animations are pending (e.g. the student polar-area flower).
 *
 * After both conditions hold, an additional `SETTLING_DELAY_MS` buffer passes
 * so the UI has a beat to breathe before a dialog appears. Charts opt in via
 * `registerPendingAnimation()` / `markAnimationComplete()` — pages without
 * animated charts never touch the set, so they settle on the session gate alone.
 */

const SETTLING_DELAY_MS = 500;

const pendingAnimations = ref(new Set<symbol>());

export const registerPendingAnimation = (): symbol => {
  const token = Symbol('pending-animation');
  pendingAnimations.value = new Set([...pendingAnimations.value, token]);
  return token;
};

export const markAnimationComplete = (token: symbol): void => {
  if (!pendingAnimations.value.has(token)) return;
  const next = new Set(pendingAnimations.value);
  next.delete(token);
  pendingAnimations.value = next;
};

export function useGrowthReadiness() {
  const { isLoggedIn } = useAuth();
  const { currentTenant } = useCurrentTenant();
  const { whoAmI, isWhoAmIDataPending } = useWhoAmI();
  const { classesOrGroups, areClassesOrGroupsPending } = useSchoolClasses();

  const isCoreSessionReady = computed(
    () =>
      isLoggedIn.value &&
      !isWhoAmIDataPending.value &&
      !areClassesOrGroupsPending.value &&
      !!currentTenant.value &&
      !!whoAmI.value &&
      !!classesOrGroups.value,
  );

  const isSettled = computed(() => isCoreSessionReady.value && pendingAnimations.value.size === 0);

  const isGrowthReady = ref(false);
  let armTimer: ReturnType<typeof setTimeout> | null = null;

  const clearArm = (): void => {
    if (armTimer) {
      clearTimeout(armTimer);
      armTimer = null;
    }
  };

  watch(
    isSettled,
    (settled) => {
      clearArm();
      if (settled) {
        armTimer = setTimeout(() => {
          isGrowthReady.value = true;
          armTimer = null;
        }, SETTLING_DELAY_MS);
      } else {
        isGrowthReady.value = false;
      }
    },
    { immediate: true },
  );

  onBeforeUnmount(() => {
    clearArm();
  });

  return {
    isGrowthReady: computed(() => isGrowthReady.value),
    registerPendingAnimation,
    markAnimationComplete,
  };
}
